import React, { useRef } from 'react';
import { describeElement } from '../shared/elements';

const Section = ({ label, children }) => (
  <div style={{ marginBottom: 18 }}>
    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.8, color: '#6b7280', marginBottom: 8 }}>{label}</div>
    {children}
  </div>
);

const btn = {
  display: 'block', width: '100%', padding: '8px 10px', fontSize: 13, fontWeight: 600,
  border: 0, borderRadius: 4, cursor: 'pointer', color: '#fff', marginBottom: 6,
};

// Preset canvas sizes tuned for our dual delivery targets — Twilio MMS + email.
// 600×600 is the recommended default (renders 1:1 in both channels and keeps
// files under the ~500KB Twilio-friendly ceiling with per-frame palettes).
const CANVAS_PRESETS = [
  { label: '600×600 (recommended)', width: 600, height: 600 },
  { label: '500×500 (smaller file)', width: 500, height: 500 },
  { label: '600×750 (portrait)',    width: 600, height: 750 },
];

export default function LeftSidebar({
  template, savedTemplates,
  onNameChange, onLoadTemplate, onNew, onSave,
  onUploadBackground, onAddText, onAddPhoto,
  onToggleGif, onDimensionsChange,
  onPackageChange, packages,
  elements, selectedId, onSelectElement, onDeleteElement,
  currentFrame,
  saving, dirty,
}) {
  const fileRef = useRef();

  return (
    <div style={{ width: 220, borderRight: '1px solid #e5e7eb', padding: 16, overflowY: 'auto', background: '#fff' }}>
      <Section label="TEMPLATE">
        <input
          type="text"
          value={template.name}
          onChange={(e) => onNameChange(e.target.value)}
          style={{ width: '100%', padding: '6px 8px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 4, marginBottom: 8 }}
        />
        <select
          value=""
          onChange={(e) => e.target.value && onLoadTemplate(Number(e.target.value))}
          style={{ width: '100%', padding: '6px 8px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 4, marginBottom: 8 }}
        >
          <option value="">— Load existing —</option>
          {savedTemplates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <div style={{ display: 'flex', gap: 6 }}>
          <button style={{ ...btn, background: '#0ea5e9', marginBottom: 0 }} onClick={onSave} disabled={saving}>
            {saving ? 'Saving…' : (dirty ? '● Save' : 'Save')}
          </button>
          <button style={{ ...btn, background: '#374151', marginBottom: 0 }} onClick={onNew}>New</button>
        </div>
      </Section>

      <Section label="CANVAS SIZE">
        <select
          value={CANVAS_PRESETS.find((p) => p.width === template.width && p.height === template.height) ? `${template.width}×${template.height}` : 'custom'}
          onChange={(e) => {
            const preset = CANVAS_PRESETS.find((p) => `${p.width}×${p.height}` === e.target.value);
            if (preset && onDimensionsChange) onDimensionsChange({ width: preset.width, height: preset.height });
          }}
          style={{ width: '100%', padding: '6px 8px', fontSize: 12, border: '1px solid #d1d5db', borderRadius: 4, marginBottom: 6 }}
        >
          {CANVAS_PRESETS.map((p) => (
            <option key={`${p.width}×${p.height}`} value={`${p.width}×${p.height}`}>{p.label}</option>
          ))}
          <option value="custom" disabled>Custom (edit below)</option>
        </select>
        <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
          <input
            type="number"
            min={1}
            value={template.width}
            onChange={(e) => onDimensionsChange && onDimensionsChange({ width: Number(e.target.value) || template.width })}
            style={{ width: '50%', padding: '4px 6px', fontSize: 12, border: '1px solid #d1d5db', borderRadius: 4 }}
            title="Width (px)"
          />
          <input
            type="number"
            min={1}
            value={template.height}
            onChange={(e) => onDimensionsChange && onDimensionsChange({ height: Number(e.target.value) || template.height })}
            style={{ width: '50%', padding: '4px 6px', fontSize: 12, border: '1px solid #d1d5db', borderRadius: 4 }}
            title="Height (px)"
          />
        </div>
        <div style={{ fontSize: 10, lineHeight: 1.4, color: '#6b7280', background: '#f9fafb', padding: '6px 8px', borderRadius: 4, border: '1px solid #e5e7eb' }}>
          <div style={{ fontWeight: 700, color: '#374151', marginBottom: 3 }}>Delivery targets</div>
          <div>• Twilio MMS: <b>≤ 500 KB</b> (≤ 300 KB safer)</div>
          <div>• Stripo/Mailgun email: <b>≤ 1 MB</b></div>
          <div>• GIF frame delay: <b>~100 ms</b> uniform</div>
          <div>• Frame 1 = Outlook fallback still image</div>
        </div>
      </Section>

      <Section label="BACKGROUND">
        <button style={{ ...btn, background: '#7c3aed' }} onClick={() => fileRef.current?.click()}>📤 Upload Background</button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => { if (e.target.files?.[0]) { onUploadBackground(e.target.files[0]); e.target.value = ''; } }}
        />
        <div style={{ fontSize: 11, color: '#6b7280' }}>
          {currentFrame?.backgroundUrl || currentFrame?.backgroundFile ? `${template.width}×${template.height}px` : 'No background yet'}
        </div>
      </Section>

      <Section label="ADD ELEMENTS">
        <button style={{ ...btn, background: '#2563eb' }} onClick={onAddPhoto}>👤 Add Photo Placeholder</button>
        <button style={{ ...btn, background: '#059669' }} onClick={onAddText}>✏ Add Text</button>
      </Section>

      <Section label="OUTPUT FORMAT">
        <label style={{ display: 'flex', alignItems: 'center', fontSize: 13, gap: 6, cursor: 'pointer' }}>
          <input type="checkbox" checked={template.output_format === 'gif'} onChange={onToggleGif} />
          Animated GIF
        </label>
      </Section>

      <Section label="PRICELIST">
        <select
          value={template.package_id ?? ''}
          onChange={(e) => onPackageChange && onPackageChange(e.target.value === '' ? null : Number(e.target.value))}
          style={{ width: '100%', padding: '6px 8px', fontSize: 12, border: '1px solid #d1d5db', borderRadius: 4, marginBottom: 4 }}
        >
          <option value="">— Unassigned (shared library) —</option>
          {(packages || []).map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <div style={{ fontSize: 10, color: '#6b7280' }}>
          Assigned templates appear in the Favorites Layout for that pricelist. Unassigned templates show under every pricelist.
        </div>
      </Section>

      <Section label={`ELEMENTS (${elements.length})`}>
        {elements.length === 0 && <div style={{ fontSize: 12, color: '#9ca3af' }}>No elements yet</div>}
        {elements.map((el) => {
          const { title, meta } = describeElement(el);
          const active = el.id === selectedId;
          return (
            <div
              key={el.id}
              onClick={() => onSelectElement(el.id)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '6px 8px', fontSize: 12, borderRadius: 4, cursor: 'pointer',
                background: active ? '#e0f2fe' : 'transparent',
                border: active ? '1px solid #0ea5e9' : '1px solid transparent',
                marginBottom: 2,
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                {el.type === 'photo' ? '👤' : '✏'} {title}
              </span>
              <span style={{ fontSize: 10, color: '#6b7280', marginLeft: 6 }}>{meta}</span>
              <button
                onClick={(e) => { e.stopPropagation(); onDeleteElement(el.id); }}
                title="Delete"
                style={{ background: 'none', border: 0, color: '#ef4444', fontSize: 14, cursor: 'pointer', marginLeft: 4 }}
              >×</button>
            </div>
          );
        })}
      </Section>
    </div>
  );
}
